import { BsTruck } from "react-icons/bs";

/**
 * Courier progress for a parcel that has been handed to OnWay.
 *
 * Only the states a customer can act on are spelled out; the rest fall back to
 * OnWay's own label. Cancelled dispatches never reach here — the API omits
 * them, since re-booking a parcel is our business and not something to alarm
 * the customer with.
 */
const STAGES = {
  37: { label: "გაფორმებულია", tone: "slate" },
  1:  { label: "გაფორმებულია", tone: "slate" },
  18: { label: "გადაეცა კურიერს", tone: "blue" },
  19: { label: "კურიერის საწყობშია", tone: "blue" },
  42: { label: "გაგზავნილია ფილიალში", tone: "blue" },
  49: { label: "ფილიალშია", tone: "blue" },
  21: { label: "გზაშია", tone: "amber" },
  44: { label: "ხელახლა გამოვა", tone: "amber" },
  51: { label: "დაყოვნებულია", tone: "amber" },
  // Courier-side pickup churn. The customer's parcel has not moved and nothing
  // is wrong from their side, so these read as "being prepared" rather than
  // OnWay's own wording — "აღების გაუქმება" on a customer's card looks like
  // their delivery was cancelled, which it was not.
  38: { label: "მზადდება გასატანად", tone: "slate" },
  52: { label: "მზადდება გასატანად", tone: "slate" },
  53: { label: "მზადდება გასატანად", tone: "slate" },
  34: { label: "ვერ ჩაბარდა", tone: "red" },
  43: { label: "ვერ ჩაბარდა", tone: "red" },
  41: { label: "ბრუნდება უკან", tone: "red" },
  22: { label: "ჩაბარებულია", tone: "green" },
  48: { label: "ჩაბარებულია", tone: "green" },
  40: { label: "გაცემულია", tone: "green" },
};

const TONES = {
  slate: "bg-slate-100 text-slate-700 border-slate-200",
  blue:  "bg-blue-50 text-blue-800 border-blue-200",
  amber: "bg-amber-50 text-amber-800 border-amber-200",
  red:   "bg-red-50 text-red-700 border-red-200",
  green: "bg-green-50 text-green-700 border-green-200",
};

const CourierStatus = ({ pack, className = "" }) => {
  if (!pack) return null;
  const onway = pack.onway;

  let label = null;
  let tone = "slate";
  let hint = null;

  if (onway) {
    const stage = STAGES[onway.status_id];
    label = stage?.label || onway.status_name;
    tone = stage?.tone || "slate";

    // Money owed on a courier order is collected at the door, so the hint has
    // to point there rather than at the site's pay button.
    if (pack.cod_locked) {
      hint = `თანხა ${Number(pack.cod_amount).toFixed(2)} ₾ გადაიხდება კურიერთან ამანათის მიღებისას`;
    }
  } else if (
    // Not handed to the courier yet. If the only thing standing in the way is
    // the money, say so — the parcel otherwise looks stuck for no reason.
    Number(pack.status) < 4 &&
    pack.home_delivery &&
    pack.is_declared &&
    Number(pack.transport_price || 0) - Number(pack.transport_paid || 0) > 0.01
  ) {
    label = "გადასახდელია";
    tone = "amber";
    // The badge only names the state; what to DO about it goes in the hover,
    // so the card stays readable but the answer is one hover away.
    hint = "ამანათის მისაღებად დაფარეთ დავალიანება";
  }

  if (!label) return null;

  const text = `საკურიერო - ${label}`;
  const tip = hint || (onway?.tracking ? `OnWay: ${onway.tracking}` : null);

  return (
    <div className={`relative inline-block group ${className}`}>
      <div
        className={`inline-flex items-center gap-1.5 border rounded-full px-2 py-0.5
                    text-[11px] font-semibold ${TONES[tone]} ${tip ? "cursor-help" : ""}`}
        title={tip || undefined}
      >
        <BsTruck className="shrink-0" />
        <span>{text}</span>
        {onway?.tracking && (
          <span className="font-normal opacity-70">#{onway.tracking}</span>
        )}
      </div>

      {/* Styled tooltip on top of the native title — the native one is slow to
          appear and the phone-sized card gives no other room for the hint. */}
      {tip && (
        <div
          className="hidden group-hover:block absolute z-20 bottom-full left-0 mb-1
                     w-max max-w-[240px] rounded-md bg-gray_900 text-white
                     text-[11px] font-normal px-2 py-1 shadow-bs1"
        >
          {tip}
        </div>
      )}
    </div>
  );
};

export default CourierStatus;
